import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { useChartAnimation } from '../printScope.js';
import { PIE_COLORS, TOOLTIP_PROPS } from '../chartTheme.js';
import type { RikuzLineRow } from '@agio/core';

const allocationData = (lines: RikuzLineRow[]) =>
  lines.filter((l) => (l.value ?? 0) > 0).map((l) => ({ name: l.sleeve_he, value: l.value, pct: l.pct_of_total }));

export const allocationHasData = (lines: RikuzLineRow[]) => allocationData(lines).length > 0;

/** Portfolio allocation pie over the latest Rikuz lines (non-zero sleeves). */
export function AllocationPie({ lines }: { lines: RikuzLineRow[] }) {
  const animate = useChartAnimation();
  const money = useMoney();
  const fundIds = lines.map((l) => l.fund_id);
  const data = allocationData(lines);
  if (data.length === 0) return <Card title="הקצאת נכסים"><EmptyState label="אין נתונים" /></Card>;

  return (
    <Card title="הקצאת נכסים">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2} stroke="none" isAnimationActive={animate}>
            {data.map((d, i) => (
              <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number, name: string, entry) =>
              [`${money(v, fundIds)} · ${fmtPct((entry?.payload as { pct: number | null })?.pct)}`, name]
            }
          />
        </PieChart>
      </ResponsiveContainer>
      <ul className="print-legend mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {data.map((d, i) => (
          <li key={`${d.name}-${i}`} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span className="text-muted-foreground">{d.name}</span>
            <span className="ms-auto tabular-nums">{fmtPct(d.pct)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
